import React, { useState, useEffect, useRef } from "react";
import { useSocket } from "../../../contexts/SocketContext";
import { Eye, Radio } from "lucide-react";
import WaitingRoom from "./videosdk/WaitingRoom";

const LiveTv = () => {
  const [showGlitch, setShowGlitch] = useState(true);
  const [isTvOn, setIsTvOn] = useState(false);
  const [screenAnimation, setScreenAnimation] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordVerified, setPasswordVerified] = useState(false);
  const [isLive, setIsLive] = useState(false);

  // OTP Input States
  const [otp, setOtp] = useState(["", "", "", ""]);
  const inputsRef = useRef([]);

  // Streaming states
  const localVideoRef = useRef();
  const remoteVideoRef = useRef();
  const [isStreaming, setIsStreaming] = useState(false);
  const [showStartBtn, setShowStartBtn] = useState(false);
  const [preRecordedVideo, setPreRecordedVideo] = useState(true);
  const [peerConnection, setPeerConnection] = useState(null);
  const [showWatchNowBtn, setShowWatchNowBtn] = useState(false);
  const [isWatching, setIsWatching] = useState(false);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isVideoStopped, setIsVideoStopped] = useState(false);
  const [currentFacingMode, setCurrentFacingMode] = useState("user");
  const [currentStream, setCurrentStream] = useState(null);

  const socket = useSocket();

  // Handle ON/OFF animation
  useEffect(() => {
    if (isTvOn) {
      setShowGlitch(true);
      setScreenAnimation(true);
      const timer = setTimeout(() => {
        setShowGlitch(false);
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      // When turning off
      setScreenAnimation(true);
      const timer = setTimeout(() => {
        setShowGlitch(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isTvOn]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("user") === "admin") setIsAdmin(true);
  }, []);

  //handleGoLive
  const handleGoLive = () => {
    if (!isTvOn) return; // Do nothing if TV is off

    const url = new URL(window.location);
    const params = url.searchParams;

    if (params.get("user") === "admin") {
      // Remove `?user=admin`
      params.delete("user");
      setIsAdmin(false);
    } else {
      // Add `?user=admin`
      params.set("user", "admin");
      setIsAdmin(true);
    }

    // Build new URL
    const newQuery = params.toString();
    const newUrl = newQuery ? `${url.pathname}?${newQuery}` : url.pathname;

    // Update URL without reloading
    window.history.replaceState({}, "", newUrl);
  };

  const handlePasswordCheck = async () => {
    if (password === "qwer1234") {
      setPasswordVerified(true);
    } else {
      alert("Wrong password");
    }
  };

  // Socket IO event handlers
  useEffect(() => {
    if (!socket) return;

    socket.emit("join");

    socket.on("live_status", (data) => {
      setIsStreaming(data.isStreaming);
      if (data.isStreaming) {
        setPreRecordedVideo(false);
        // Show "Watch Now" button for viewers when stream starts
        if (!isAdmin) {
          setShowWatchNowBtn(true);
        }
      } else {
        setPreRecordedVideo(true);
        setShowWatchNowBtn(false);
        setIsWatching(false);
      }
    });

    // Listen for streaming started event (when admin starts)
    socket.on("streaming_started", () => {
      setIsStreaming(true);
      setPreRecordedVideo(false);
      if (!isAdmin) {
        setShowWatchNowBtn(true);
      }
    });

    // Listen for live stream started event
    socket.on("live_stream_started", (data) => {
      console.log("Stream started:", data);
      setIsStreaming(true);
      setPreRecordedVideo(false);
      if (!isAdmin) {
        setShowWatchNowBtn(true);
      }
    });

    // Listen for viewer count updates
    socket.on("viewer_count_update", (data) => {
      setViewerCount(data.count);
    });

    // Admin: Handle viewer requesting stream
    socket.on("viewer_requesting_stream", async ({ viewerId }) => {
      if (!peerConnection || !isAdmin) return;

      console.log("Viewer requesting stream:", viewerId);

      try {
        const offer = await peerConnection.createOffer();
        await peerConnection.setLocalDescription(offer);

        socket.emit("offer", {
          sdp: offer,
          to: viewerId,
          from: socket.id,
        });
      } catch (error) {
        console.error("Error creating offer:", error);
      }
    });

    socket.on("offer", async ({ sdp, from }) => {
      // Only viewers receive offers from admin
      if (isAdmin) return;

      const pc = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
      });

      setPeerConnection(pc);

      // Viewer receives tracks from admin (receive-only)
      pc.ontrack = (event) => {
        console.log("📺 Receiving stream from admin:", event.streams[0]);
        setTimeout(() => {
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = event.streams[0];
          }
        }, 0);
      };

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          socket.emit("ice-candidate", {
            candidate: event.candidate,
            to: from,
          });
        }
      };

      // Create answer without adding any tracks (receive-only)
      await pc.setRemoteDescription(new RTCSessionDescription(sdp));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      socket.emit("answer", { sdp: answer, to: from, from: socket.id });
    });

    socket.on("answer", async ({ sdp }) => {
      if (!peerConnection) return;
      await peerConnection.setRemoteDescription(new RTCSessionDescription(sdp));
    });

    socket.on("ice-candidate", async (candidate) => {
      if (!peerConnection) return;
      await peerConnection.addIceCandidate(candidate);
    });
    return () => {
      socket.off("live_status");
      socket.off("streaming_started");
      socket.off("live_stream_started");
      socket.off("viewer_count_update");
      socket.off("viewer_requesting_stream");
      socket.off("offer");
      socket.off("answer");
      socket.off("ice-candidate");
    };
  }, [socket, peerConnection, isAdmin]);

  const handleStartStreaming = async () => {
    try {
      // Request video and audio from admin's device
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 },
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      console.log("🎥 Admin starting stream with:", stream);

      // Set streaming to true first to render the video element
      setIsStreaming(true);
      socket.emit("live_stream_started");

      // Wait for the video element to be rendered
      setTimeout(() => {
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
          localVideoRef.current.muted = true;
        }
      }, 0);

      // Create peer connection with admin's stream
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
      });

      // Add admin's video and audio tracks to the connection
      stream.getTracks().forEach((track) => {
        console.log(`➕ Adding track: ${track.kind}`);
        pc.addTrack(track, stream);
      });

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          socket.emit("ice-candidate", {
            candidate: event.candidate,
            to: null,
          });
        }
      };

      setPeerConnection(pc);
      setCurrentStream(stream);
      setShowStartBtn(false);
    } catch (error) {
      console.error("❌ Error starting stream:", error);
      alert("Failed to access camera/microphone. Please grant permissions.");
    }
  };

  // Toggle audio mute/unmute
  const toggleAudioMute = () => {
    if (currentStream) {
      const audioTrack = currentStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioMuted(!audioTrack.enabled);
        console.log(`🔊 Audio ${audioTrack.enabled ? "unmuted" : "muted"}`);
      }
    }
  };

  // Toggle video on/off
  const toggleVideo = () => {
    if (currentStream) {
      const videoTrack = currentStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoStopped(!videoTrack.enabled);
        console.log(`📹 Video ${videoTrack.enabled ? "enabled" : "disabled"}`);
      }
    }
  };

  // Rotate camera (switch between front and back)
  const rotateCamera = async () => {
    try {
      // Stop current stream
      if (currentStream) {
        currentStream.getTracks().forEach((track) => track.stop());
      }

      // Toggle facing mode
      const newFacingMode =
        currentFacingMode === "user" ? "environment" : "user";

      // Get new stream with opposite camera
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: newFacingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 },
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      console.log(`🔄 Camera rotated to: ${newFacingMode}`);

      // Update video element
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = newStream;
      }

      // Replace tracks in peer connection
      if (peerConnection) {
        const senders = peerConnection.getSenders();
        const videoSender = senders.find(
          (sender) => sender.track?.kind === "video"
        );
        const audioSender = senders.find(
          (sender) => sender.track?.kind === "audio"
        );

        if (videoSender) {
          videoSender.replaceTrack(newStream.getVideoTracks()[0]);
        }
        if (audioSender) {
          audioSender.replaceTrack(newStream.getAudioTracks()[0]);
        }
      }

      setCurrentStream(newStream);
      setCurrentFacingMode(newFacingMode);

      // Restore mute states
      if (isAudioMuted) {
        newStream.getAudioTracks()[0].enabled = false;
      }
      if (isVideoStopped) {
        newStream.getVideoTracks()[0].enabled = false;
      }
    } catch (error) {
      console.error("❌ Error rotating camera:", error);
      alert(
        "Failed to rotate camera. Your device may not have multiple cameras."
      );
    }
  };

  const handleStopStreaming = () => {
    if (peerConnection) {
      peerConnection.getSenders().forEach((s) => s.track.stop());
      peerConnection.close();
      setPeerConnection(null);
    }

    if (currentStream) {
      currentStream.getTracks().forEach((track) => track.stop());
      setCurrentStream(null);
    }

    if (localVideoRef.current && localVideoRef.current.srcObject) {
      localVideoRef.current.srcObject
        .getTracks()
        .forEach((track) => track.stop());
    }

    socket.emit("stop_stream");
    setIsStreaming(false);
    setShowStartBtn(true);
    setIsAudioMuted(false);
    setIsVideoStopped(false);
  };

  const handleWatchNow = async () => {
    // Get browser and user info
    const userAgent = navigator.userAgent;
    const browserInfo = {
      platform: navigator.platform,
      language: navigator.language,
      userAgent: userAgent,
      browser: getBrowserName(userAgent),
      screenResolution: `${window.screen.width}x${window.screen.height}`,
    };

    // Send viewer details to server
    socket.emit("viewer_joined", {
      viewerId: socket.id,
      timestamp: new Date().toISOString(),
      browserInfo: browserInfo,
    });

    setShowWatchNowBtn(false);
    setIsWatching(true);

    // Create receive-only peer connection for viewer
    try {
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
      });

      // Viewer only receives tracks (no sending)
      pc.ontrack = (event) => {
        console.log("📺 Received track from admin:", event.streams[0]);
        setTimeout(() => {
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = event.streams[0];
          }
        }, 0);
      };

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          socket.emit("ice-candidate", {
            candidate: event.candidate,
            to: "admin",
          });
        }
      };

      setPeerConnection(pc);

      // Send request to admin to start connection
      // Admin will create offer and send it to this viewer
      socket.emit("request_stream", { from: socket.id });
    } catch (error) {
      console.error("Error setting up peer connection:", error);
    }
  };

  // Helper function to get browser name
  const getBrowserName = (userAgent) => {
    if (userAgent.includes("Firefox")) return "Firefox";
    if (userAgent.includes("Chrome") && !userAgent.includes("Edg"))
      return "Chrome";
    if (userAgent.includes("Safari") && !userAgent.includes("Chrome"))
      return "Safari";
    if (userAgent.includes("Edg")) return "Edge";
    if (userAgent.includes("Opera") || userAgent.includes("OPR"))
      return "Opera";
    return "Unknown";
  };

  // OTP Handlers
  const handleChange = (e, index) => {
    const value = e.target.value.replace(/\D/, ""); // only digits
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 3) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const handleVerify = () => {
    const otpValue = otp.join("");
    console.log("Entered OTP:", otpValue);
    // Add verification logic here
  };

  return (
    <div className="flex-1 overflow-y-auto flex items-center justify-center bg-transparent rounded-2xl">
      <div className="relative flex w-full max-w-5xl aspect-[16/9] border-2 border-gray-800 rounded-2xl bg-black shadow-[0_0_40px_rgba(0,0,0,0.8)] overflow-hidden">
        {/* TV Frame */}
        <div className="flex w-full h-full">
          {/* TV Screen */}
          <div className="w-3/4 bg-black flex items-center justify-center border-r-8 border-gray-700">
            <div className="relative w-[95%] h-[90%] bg-gray-950 rounded-md overflow-hidden flex items-center justify-center shadow-inner">
              {/* Admin Page*/}
              {isAdmin ? (
                <div>
                  {/*  Password verification  */}
                  {!passwordVerified && (
                    <>
                      <div className="flex flex-col justify-center items-center gap-2">
                        <input
                          type="password"
                          placeholder="Enter password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="p-3 text-gray-400 bg-gray-800 rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-primary"
                        />

                        <button
                          className="px-4 py-2 bg-primary hover:bg-[#06bf37] text-primary-foreground rounded-md transition-colors duration-200"
                          onClick={handlePasswordCheck}
                        >
                          Verify
                        </button>
                      </div>

                      <div className="hidden flex flex-col items-center gap-2">
                        <div className="flex gap-2">
                          {otp.map((digit, index) => (
                            <input
                              key={index}
                              ref={(el) => (inputsRef.current[index] = el)}
                              type="text"
                              inputMode="numeric"
                              maxLength={1}
                              value={digit}
                              onChange={(e) => handleChange(e, index)}
                              onKeyDown={(e) => handleKeyDown(e, index)}
                              className="w-12 h-12 text-center text-white bg-gray-800 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                          ))}
                        </div>
                        <button
                          onClick={handleVerify}
                          className="px-4 py-2 bg-primary hover:bg-[#06bf37] text-primary-foreground rounded-md transition-colors duration-200"
                        >
                          Verify
                        </button>
                      </div>
                    </>
                  )}

                  {/* Admin start streaming button */}
                  {passwordVerified && !isStreaming && (
                    <div>
                      <button
                        onClick={handleStartStreaming}
                        style={{
                          padding: "15px 30px",
                          fontSize: "18px",
                          backgroundColor: "#00aa00",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          cursor: "pointer",
                        }}
                      >
                        Start Streaming
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* Old TV / Glitch */}
                  {/* <WaitingRoom /> */}
                  <div
                    className={`absolute inset-0 transition-opacity duration-1000 ${
                      showGlitch ? "opacity-100" : "opacity-0"
                    }`}
                  >
                    <video
                      className="w-full h-full object-cover"
                      src="https://www.shutterstock.com/shutterstock/videos/3793609367/preview/stock-footage-analog-tv-screen-noise-glitch-effect-background-glitch-static-noise-television-old-film-effect.webm"
                      autoPlay
                      muted
                      loop
                    ></video>
                  </div>

                  {/* Watch Now button for viewers when stream starts */}
                  {!isAdmin && showWatchNowBtn && !isWatching && (
                    <div style={{ marginTop: "20px" }}>
                      <h2 style={{ color: "#ff0000" }}>
                        🔴 Live Stream is Now Available!
                      </h2>
                      <button
                        onClick={handleWatchNow}
                        style={{
                          padding: "15px 30px",
                          fontSize: "18px",
                          backgroundColor: "#ff0000",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          cursor: "pointer",
                          marginTop: "10px",
                          fontWeight: "bold",
                        }}
                      >
                        🎬 Watch Now
                      </button>
                    </div>
                  )}

                  {/* Streaming video display */}
                  {isStreaming && (
                    <div>
                      {isAdmin && (
                        <div style={{ marginBottom: "10px" }}>
                          <button
                            onClick={handleStopStreaming}
                            style={{
                              padding: "10px 20px",
                              fontSize: "16px",
                              backgroundColor: "#cc0000",
                              color: "white",
                              border: "none",
                              borderRadius: "5px",
                              cursor: "pointer",
                            }}
                          >
                            Stop Streaming
                          </button>
                        </div>
                      )}
                      <div className="flex flex-col justify-center items-center">
                        {isAdmin ? (
                          <>
                            <h3 className="mb-2">Your Stream (Admin View)</h3>
                            <video
                              ref={localVideoRef}
                              autoPlay
                              muted
                              width="600"
                              style={{ border: "2px solid red" }}
                            />

                            {/* Admin Controls */}
                            <div
                              style={{
                                display: "flex",
                                gap: "10px",
                                marginTop: "15px",
                                flexWrap: "wrap",
                                justifyContent: "center",
                              }}
                            >
                              <button
                                onClick={toggleAudioMute}
                                style={{
                                  padding: "10px 20px",
                                  fontSize: "14px",
                                  backgroundColor: isAudioMuted
                                    ? "#ff6b6b"
                                    : "#4CAF50",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "5px",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "5px",
                                }}
                              >
                                {isAudioMuted
                                  ? "🔇 Unmute Audio"
                                  : "🔊 Mute Audio"}
                              </button>

                              <button
                                onClick={toggleVideo}
                                style={{
                                  padding: "10px 20px",
                                  fontSize: "14px",
                                  backgroundColor: isVideoStopped
                                    ? "#ff6b6b"
                                    : "#2196F3",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "5px",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "5px",
                                }}
                              >
                                {isVideoStopped
                                  ? "📹 Enable Video"
                                  : "📹 Disable Video"}
                              </button>

                              <button
                                onClick={rotateCamera}
                                style={{
                                  padding: "10px 20px",
                                  fontSize: "14px",
                                  backgroundColor: "#FF9800",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "5px",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "5px",
                                }}
                              >
                                🔄 Rotate Camera
                              </button>
                            </div>

                            <div
                              style={{
                                marginTop: "10px",
                                fontSize: "12px",
                                color: "#666",
                              }}
                            >
                              <p>
                                {isAudioMuted && "⚠️ Audio is muted | "}
                                {isVideoStopped && "⚠️ Video is disabled | "}
                                📱 Camera:{" "}
                                {currentFacingMode === "user"
                                  ? "Front"
                                  : "Back"}
                              </p>
                            </div>
                          </>
                        ) : isWatching ? (
                          <>
                            <h3>🔴 Live Stream</h3>
                            <video
                              ref={remoteVideoRef}
                              autoPlay
                              playsInline
                              width="600"
                              style={{
                                border: "2px solid green",
                                backgroundColor: "#000",
                                transition: "opacity 0.3s ease",
                              }}
                              controls={false}
                            />

                            {/* Viewer Controls */}
                            <div
                              style={{
                                display: "flex",
                                gap: "10px",
                                marginTop: "15px",
                                flexWrap: "wrap",
                                justifyContent: "center",
                              }}
                            >
                              <button
                                onClick={() => {
                                  if (remoteVideoRef.current) {
                                    remoteVideoRef.current.muted =
                                      !remoteVideoRef.current.muted;
                                    setIsAudioMuted(
                                      remoteVideoRef.current.muted
                                    );
                                  }
                                }}
                                style={{
                                  padding: "10px 20px",
                                  fontSize: "14px",
                                  backgroundColor: isAudioMuted
                                    ? "#ff6b6b"
                                    : "#4CAF50",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "5px",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "5px",
                                }}
                              >
                                {isAudioMuted
                                  ? "🔇 Unmute Audio"
                                  : "🔊 Mute Audio"}
                              </button>

                              <button
                                onClick={() => {
                                  if (remoteVideoRef.current) {
                                    if (isVideoStopped) {
                                      remoteVideoRef.current.style.opacity =
                                        "1";
                                      setIsVideoStopped(false);
                                    } else {
                                      remoteVideoRef.current.style.opacity =
                                        "0";
                                      setIsVideoStopped(true);
                                    }
                                  }
                                }}
                                style={{
                                  padding: "10px 20px",
                                  fontSize: "14px",
                                  backgroundColor: isVideoStopped
                                    ? "#ff6b6b"
                                    : "#2196F3",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "5px",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "5px",
                                }}
                              >
                                {isVideoStopped
                                  ? "📹 Show Video"
                                  : "📹 Hide Video"}
                              </button>
                            </div>

                            <div
                              style={{
                                marginTop: "10px",
                                fontSize: "12px",
                                color: "#666",
                              }}
                            >
                              <p>
                                🔇 Viewer mode - You are watching the stream
                                {isAudioMuted && " | ⚠️ Audio is muted"}
                                {isVideoStopped && " | ⚠️ Video is hidden"}
                              </p>
                            </div>
                          </>
                        ) : null}
                      </div>
                    </div>
                  )}

                  {/* Video */}
                  <div
                    className={`absolute inset-0 transition-opacity duration-1000 ${
                      !showGlitch ? "opacity-100" : "opacity-0"
                    }`}
                  >
                    <video
                      className="w-full h-full object-cover"
                      src="https://www.w3schools.com/html/mov_bbb.mp4"
                      autoPlay
                      muted
                      loop
                    ></video>
                  </div>

                  {/* LIVE badge */}
                  {isLive && (
                    <div className="absolute top-3 right-4 flex justify-center items-center rounded-full overflow-hidden backdrop-blur-md bg-gray-800/40 border border-white/10">
                      <span className="text-sm bg-red-600/90 text-white px-3 py-1 flex items-center justify-center h-full">
                        LIVE
                      </span>
                      <span className="flex justify-center items-center gap-1 text-[12px] text-white px-2 py-1 h-full">
                        <Eye size={14} /> {viewerCount}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Control Panel */}
          <div className="w-1/4 bg-gray-black flex flex-col justify-between p-2 border-l-[6px] border-gray-900">
            {/* Speaker */}
            <div className="grid grid-cols-6 gap-1 mb-4">
              {Array.from({ length: 36 }).map((_, i) => (
                <div
                  key={i}
                  className="flex items-center justify-center border p-1"
                >
                  <div className="w-3 h-3 bg-black rounded-full shadow-[inset_0_0_2px_rgba(255,255,255,0.2)] border"></div>
                </div>
              ))}
            </div>

            {/* Control Section */}
            <div className="space-y-4 bg-[#b2afae] p-2 rounded-md border-8 border-black-300 shadow-[inset_0_0_10px_rgba(0,0,0,0.5)]">
              <div className="flex flex-row justify-between gap-3">
                {/* Sound knobs */}
                <div className="flex flex-col justify-around gap-3 w-full">
                  {[1, 2].map((i) => (
                    <div
                      key={i}
                      className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-200 to-gray-500 border border-gray-700 flex items-center justify-center shadow-[inset_-2px_-2px_3px_rgba(0,0,0,0.4)] hover:rotate-6 transition-transform duration-150"
                    >
                      <div className="w-2 h-2 bg-gray-700 rounded-full"></div>
                    </div>
                  ))}
                </div>

                {/* Channel Changer */}
                <div className="flex items-center justify-center">
                  <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-gray-300 to-gray-600 border-2 border-gray-700 shadow-[inset_2px_2px_3px_rgba(0,0,0,0.3)] hover:rotate-[10deg] transition-transform duration-300">
                    <div className="absolute top-[6px] left-1/2 -translate-x-1/2 w-1 h-3 bg-gray-900 rounded-sm"></div>
                  </div>
                </div>
              </div>

              {/* Power Control */}
              <div className="flex items-center justify-around pt-2">
                {/* Power Button */}
                <div
                  onClick={() => {
                    if (isAdmin) return;
                    setIsTvOn(!isTvOn);
                  }}
                  className={`flex flex-col items-center cursor-pointer`}
                >
                  <div
                    className={`w-6 h-10 rounded-sm border-2 border-gray-700 flex justify-center transition-all duration-300 ${
                      isTvOn ? "bg-green-700" : "bg-gray-900"
                    }`}
                  >
                    <div
                      className={`w-4 h-2 rounded-sm transition-all duration-300 ${
                        isTvOn ? "bg-green-400 mt-1" : "bg-gray-500 mt-6"
                      }`}
                    ></div>
                  </div>
                  <p className="text-[10px] text-black mt-1">POWER</p>
                </div>

                {/* Go Live Button */}
                <div
                  onClick={handleGoLive}
                  className={`flex justify-center items-center ${
                    !isTvOn ? "cursor-not-allowed" : "cursor-pointer"
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex flex-col justify-center items-center transition-colors duration-300 
                  ${!isTvOn ? "bg-gray-600" : "bg-green-600"}`}
                  >
                    <Radio size={12} />
                    <span className="text-[7px] text-white font-bold">
                      LIVE
                    </span>
                  </div>
                </div>

                {/* Power Indicator */}
                <div
                  className={`w-4 h-4 rounded-full transition-all duration-700 shadow-inner ${
                    isTvOn
                      ? "bg-red-600 brightness-125 shadow-red-500"
                      : "bg-red-800 opacity-30"
                  }`}
                ></div>
              </div>
            </div>

            {/* Bottom: Logo */}
            <div className="pt-4 text-center border-t border-gray-700">
              <p className="text-xs tracking-wider text-gray-200 font-bold font-mono">
                NoobTV
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveTv;
